import { CTA_TOOL_CLASS_NAME, CTA_TOOL_VARIANT_CLASSNAME } from '@/atoms/CtaTool';
import { CTA_TOOL_VARIANT_ENUM } from '@/atoms/CtaTool/type';
import CTATools, { CTAToolType } from '@/molecules/CTATools';
import DropDown from '@/molecules/DropDown';
import { useMediaQuery } from '@react-hookz/web';
import clx from 'classnames';
import useCardDetailsApi from 'hooks/queries/cards/useCardsDetailsApi';
import useCardActions from 'hooks/useCardActions';
import { useTranslation } from 'react-i18next';
import { CARD_TYPE_API_REF } from 'types/modifiers/references';
import { useCmsData } from 'utils/cms';
import { useEffect, useRef } from 'react';

interface CardCTAToolsProps {
  reference: string;
  className?: string;
}

const CardCTATools = ({ reference, className }: CardCTAToolsProps) => {
  const { t } = useTranslation();
  const { card, queryKey, isLoading } = useCardDetailsApi(reference);
  const containerRef = useRef<HTMLDivElement>(null);
  const { handleClickWantlist, handleClickGive, handleApplyFoiler, handleClickSell, handleTradeList } = useCardActions({
    reference,
    queryKey,
    cardId: card['@id'],
  });

  const { generalSettings } = useCmsData();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    if (!isLoading && containerRef.current) {
      const firstButton = containerRef.current.querySelector('button');
      firstButton?.focus();
    }
  }, [isLoading]);

  if (isLoading) {
    return null;
  }

  const ctaToolsItems: CTAToolType[] = [
    ...(generalSettings.marketplace && !!card?.stats?.inMyCollection
      ? [
          {
            key: 'sell-a-card',
            onClick: handleClickSell,
            children: t('sell-copy'),
            icon: 'fa-solid fa-hand-holding-dollar',
          },
        ]
      : []),
    {
      key: 'add-remove-to-wantlist',
      onClick: () => handleClickWantlist(card?.stats?.inMyWantlist),
      children: card?.stats?.inMyWantlist ? t('remove-to-wantlist') : t('add-to-wantlist'),
      icon: card?.stats?.inMyWantlist ? 'fa-solid fa-heart' : 'fa-regular fa-heart',
    },
    ...(generalSettings.give_cards && card?.stats?.inMyCollection
      ? [
          {
            key: 'trade-list',
            onClick: () => handleTradeList(card),
            children: t('trade-list'),
            icon: 'fa-solid fa-hand-holding-hand',
          },
        ]
      : []),
    ...(generalSettings.give_cards && !!card?.stats?.inMyCollection
      ? [
          {
            key: 'give-a-card',
            onClick: handleClickGive,
            children: t('give-a-card'),
            icon: 'fa-solid fa-hand-holding-heart',
          },
        ]
      : []),
    ...(!!(card?.cardType?.reference !== CARD_TYPE_API_REF.FOILER) && !card?.stats?.foiled && !!generalSettings.foiler
      ? [
          {
            key: 'apply-foiler',
            onClick: handleApplyFoiler,
            children: t('foiler'),
            icon: 'fa-solid fa-sparkles',
          },
        ]
      : []),
  ];

  if (isDesktop) {
    return (
      <div ref={containerRef}>
        <CTATools className={className} items={ctaToolsItems} />
      </div>
    );
  }

  const mainTools = ctaToolsItems.slice(0, 2);
  const dropdownTools = ctaToolsItems.slice(2);

  return (
    <div ref={containerRef} className="flex items-center gap-2">
      <CTATools className={className} items={mainTools} />
      {dropdownTools.length > 0 && (
        <DropDown
          align="end"
          items={dropdownTools.map((tool) => (
            <button key={tool.key} className="flex items-center gap-2 w-full" onClick={tool.onClick} type="button">
              <i className={tool.icon} />
              <span>{tool.children}</span>
            </button>
          ))}
          trigger={
            <button
              className={clx(
                CTA_TOOL_CLASS_NAME,
                CTA_TOOL_VARIANT_CLASSNAME[CTA_TOOL_VARIANT_ENUM.INFO],
                'pointer-events-auto h-full',
              )}
              type="button"
            >
              <i className="fa-solid fa-ellipsis-vertical" />
              {t('see-more')}
            </button>
          }
        />
      )}
    </div>
  );
};

export default CardCTATools;
